/**
 * Runtime environment configuration
 * Values loaded from /config/env-config.json (mounted from ConfigMap in K8s)
 */

export interface EnvConfig {
  apiBaseUrl: string;
  siteId: string;
  stripePublishableKey: string;
  identityServerUrl: string;
  identityServerClientId: string;
  gtmId: string;
  publicSiteUrl: string;
  norwegianRedirectUrl: string;
  /** Shows the dev impersonation widget. Only set in local/test configs; the
   * backend endpoint additionally refuses to run in Production. */
  enableImpersonation: boolean;
  /** When true, login is routed through the backend's Natlink SSO start
   * endpoint instead of the legacy identity server. Per-site switch,
   * off by default until a site's backend is cut over. */
  ssoLoginEnabled: boolean;
  /**
   * Origin of WeHunt Web, the app the shared menu's WEB APP items link to.
   * One value for every shop site: there is one web app. Production by
   * default so a missing key can never point a live shop at a test app.
   */
  webAppUrl: string;
  /**
   * Raster tile template for the checkout's pick-up point map. Configured rather than hard-coded
   * so the tile provider can be changed without a code deploy: the OSM default matches what
   * nShift's own checkout widget serves, but the OSM Foundation's usage policy discourages
   * commercial traffic, so production should point at a keyed provider.
   */
  mapTileUrl: string;
  /** Attribution for `mapTileUrl`. Required by the tile licence and always rendered. */
  mapTileAttribution: string;
}

const defaults: EnvConfig = {
  apiBaseUrl: "https://testapi.wehunt.app/api/v2",
  siteId: "wehunt",
  stripePublishableKey: "",
  identityServerUrl: "https://testid.tracker-gps.com",
  identityServerClientId: "oag-development",
  gtmId: "",
  publicSiteUrl: "",
  norwegianRedirectUrl: "https://wehunt.no/nb-NO/",
  enableImpersonation: false,
  ssoLoginEnabled: false,
  webAppUrl: "https://wehuntapp.com",
  mapTileUrl: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  mapTileAttribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

let cachedConfig: EnvConfig | null = null;

/** Accepts boolean true or the string "true" (ConfigMap values are often strings). */
function parseBoolFlag(value: unknown): boolean {
  return value === true || value === "true";
}

function normalizeUrl(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  try {
    new URL(trimmed);
    return trimmed;
  } catch {
    return fallback;
  }
}

/**
 * Resolves a per-site value from a map or falls back to a single-value key.
 * Supports both formats:
 *   "KEY":  "value"                                  (legacy single value)
 *   "KEYS": { "tracker": "value", "wehunt": "value" }
 */
function resolvePerSiteValue(
  data: Record<string, unknown>,
  mapKey: string,
  singleKey: string,
  siteId: string
): string {
  const map = data[mapKey] as Record<string, string> | undefined;
  if (map && typeof map === "object" && map[siteId]) {
    return map[siteId];
  }
  if (typeof data[singleKey] === "string") {
    return data[singleKey] as string;
  }
  return "";
}

export async function loadEnv(): Promise<EnvConfig> {
  if (cachedConfig) return cachedConfig;

  try {
    // Mounted from a ConfigMap in K8s; served from public/config locally
    const response = await fetch("/config/env-config.json");
    if (response.ok) {
      const data = await response.json();
      const siteId = data.SITE_ID || defaults.siteId;
      cachedConfig = {
        apiBaseUrl: data.API_BASE_URL || defaults.apiBaseUrl,
        siteId,
        stripePublishableKey:
          resolvePerSiteValue(
            data,
            "STRIPE_PUBLISHABLE_KEYS",
            "STRIPE_PUBLISHABLE_KEY",
            siteId
          ) || defaults.stripePublishableKey,
        identityServerUrl:
          data.IDENTITY_SERVER_URL || defaults.identityServerUrl,
        identityServerClientId:
          data.IDENTITY_SERVER_CLIENT_ID || defaults.identityServerClientId,
        gtmId:
          resolvePerSiteValue(data, "GTM_IDS", "GTM_ID", siteId) ||
          defaults.gtmId,
        publicSiteUrl: data.PUBLIC_SITE_URL || defaults.publicSiteUrl,
        norwegianRedirectUrl: normalizeUrl(
          data.NORWEGIAN_REDIRECT_URL,
          defaults.norwegianRedirectUrl
        ),
        enableImpersonation: parseBoolFlag(data.ENABLE_IMPERSONATION),
        ssoLoginEnabled: parseBoolFlag(data.SSO_LOGIN_ENABLED),
        // The shared cross-app menu appends a path (e.g. "/hunts") to this
        // origin, so a surviving trailing slash would double up into
        // "//hunts".
        webAppUrl: normalizeUrl(data.WEB_APP_URL, defaults.webAppUrl).replace(
          /\/+$/,
          ""
        ),
        // Not normalizeUrl: the tile template contains {z}/{x}/{y} placeholders, which are
        // not a valid URL until Leaflet substitutes them.
        mapTileUrl:
          typeof data.MAP_TILE_URL === "string" && data.MAP_TILE_URL.trim()
            ? data.MAP_TILE_URL.trim()
            : defaults.mapTileUrl,
        mapTileAttribution:
          typeof data.MAP_TILE_ATTRIBUTION === "string" &&
          data.MAP_TILE_ATTRIBUTION.trim()
            ? data.MAP_TILE_ATTRIBUTION.trim()
            : defaults.mapTileAttribution,
      };
      return cachedConfig;
    }

    cachedConfig = defaults;
  } catch {
    cachedConfig = defaults;
  }

  return cachedConfig;
}

// Synchronous access after initial load
export function getEnv(): EnvConfig {
  return cachedConfig || defaults;
}
